import React, {PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import * as amplitude from "@amplitude/analytics-browser";
import {amplitudeToken, env} from "@bounce/shared/constants";
import {useLocation} from "react-router-dom";
import {sessionReplayPlugin} from "@amplitude/plugin-session-replay-browser";
import {AmplitudeLogger} from "@bounce/shared/helpers";
import {get} from "lodash";
import {useAuthContext} from "./AuthContext";

type GeneralEventProps = {
  url: string;
  env: string;
  error?: string;
  account_id?: string;
  user_id?: string;
  email?: string;
};
type PageClickEventProps = GeneralEventProps & {
  page: string;
  cta: string;
  event_type: string;
  data?: Record<string, any>;
};

type PageViewEventProps = GeneralEventProps & {
  page: string;
  event_type: string;
  data?: Record<string, any>;
};

export interface AmplitudeContextProps {
  pageClickEvent: ({page, cta, data}: {page: string; cta: string; data?: Record<string, any>}) => void;
  pageViewEvent: ({page, data}: {page: string; data?: Record<string, any>}) => void;
  setError: (error: string | undefined) => void;
}

const AmplitudeContext = React.createContext<AmplitudeContextProps>(undefined!);

const AmplitudeProvider = ({children}: PropsWithChildren) => {
  const enabled = ["production"].includes(env);
  const [accountId, setAccountId] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const error = useRef<string | undefined>(undefined);
  const {email, settings} = useAuthContext();
  const {search, pathname} = useLocation();

  const searchParams = new URLSearchParams(search);
  const shouldNotTrack = !enabled;
  const setError = (currError?: string) => {
    error.current = currError;
  };

  useEffect(() => {
    const searchParamsObject = Object.fromEntries(searchParams.entries());
    const currentUserId = get(searchParamsObject, "id");
    const currentAccountId = get(searchParamsObject, "accountId");
    setAccountId(currentAccountId);
    setUserId(currentUserId);
  }, [pathname]);

  const initAmplitude = () => {
    amplitude.init(amplitudeToken, {
      minIdLength: 4,
      userId: "",
      defaultTracking: {
        sessions: true,
        pageViews: false,
        formInteractions: false,
        fileDownloads: false,
      },
      loggerProvider: new AmplitudeLogger(),
    });
    const sessionReplayTracking = sessionReplayPlugin({sampleRate: 1});
    amplitude.add(sessionReplayTracking);
  };

  const generalProperties: GeneralEventProps = useMemo(() => {
    return {
      app: "partner-platform",
      url: window.location.href,
      env,
      event: "",
      user_id: userId,
      account_id: accountId,
      email: email,
      partner_code: settings?.partner_code,
    };
  }, [window.location.href, userId, accountId, email]);

  const pageViewEvent = useCallback(
    ({page, data}: {page: string; data?: Record<string, any>}) => {
      if (shouldNotTrack) {
        return;
      }
      const properties: PageViewEventProps = {
        page: page,
        event_type: "partner_page_view",
        data: data,
        ...generalProperties,
      };

      amplitude.track(properties["event_type"], properties);
    },
    [generalProperties, shouldNotTrack]
  );

  const pageClickEvent = useCallback(
    ({page, cta, data}: {page: string; cta: string; data?: Record<string, any>}): void => {
      if (shouldNotTrack) {
        return;
      }

      const properties: PageClickEventProps = {
        page: page,
        event_type: "partner_page_click",
        cta: cta,
        data: data,
        error: error.current || data?.error,
        ...generalProperties,
      };

      amplitude.track(properties["event_type"], properties);
      setError(undefined);
    },
    [generalProperties, shouldNotTrack, error]
  );

  useEffect(() => {
    if (shouldNotTrack) {
      return;
    }
    initAmplitude();
  }, []);

  useEffect(() => {
    if (shouldNotTrack) {
      return;
    }
    const currentId = amplitude.getUserId();
    if (email && !currentId) {
      amplitude.setUserId(email);
    }
  }, [email, shouldNotTrack]);

  return (
    <AmplitudeContext.Provider
      value={{
        pageClickEvent,
        pageViewEvent,
        setError,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};

const useAmplitude = (): AmplitudeContextProps => {
  const context = useContext(AmplitudeContext);

  if (!context) throw new Error("AmplitudeContext must be used inside AmplitudeProvider");

  return context;
};

export {AmplitudeContext, AmplitudeProvider, useAmplitude};
